import { type BarChartMetric } from "@vega-api/generated/types/barChartMetric";
import { type DealTrackingMetric } from "@vega-api/generated/types/dealTrackingMetric";
import { type InsightTrackingMetric } from "@vega-api/generated/types/insightTrackingMetric";
import { type PieChartMetric } from "@vega-api/generated/types/pieChartMetric";
import { type ProgressBarMetric } from "@vega-api/generated/types/progressBarMetric";

export const isPieChartMetric = (
  metric: DealTrackingMetric | InsightTrackingMetric,
): metric is PieChartMetric => "pieChartMetricDiscriminator" in metric;

export const isTopOverviewMetric = (
  metric: DealTrackingMetric,
): metric is Exclude<DealTrackingMetric, PieChartMetric | ProgressBarMetric> =>
  "countMetricDiscriminator" in metric ||
  "currencyMetricDiscriminator" in metric ||
  "percentageMetricDiscriminator" in metric ||
  "textMetricDiscriminator" in metric;

export const splitDealTrackingMetrics = (metrics: DealTrackingMetric[]) => {
  const topMetrics: DealTrackingMetric[] = [];
  const pieChartMetrics: PieChartMetric[] = [];

  for (const metric of metrics) {
    if (isPieChartMetric(metric)) {
      pieChartMetrics.push(metric);
    } else if (isTopOverviewMetric(metric)) {
      topMetrics.push(metric);
    }
  }

  return { topMetrics, pieChartMetrics };
};

export const isBarChartMetric = (
  metric: InsightTrackingMetric,
): metric is BarChartMetric => "barChartMetricDiscriminator" in metric;

export type ChartInsightTrackingMetric = BarChartMetric | PieChartMetric;

export type ScalarInsightTrackingMetric = Exclude<
  InsightTrackingMetric,
  BarChartMetric | PieChartMetric
>;

export const isScalarMetric = (
  metric: InsightTrackingMetric,
): metric is ScalarInsightTrackingMetric =>
  !isPieChartMetric(metric) && !isBarChartMetric(metric);

export const isChartMetric = (
  metric: InsightTrackingMetric,
): metric is ChartInsightTrackingMetric =>
  isBarChartMetric(metric) || isPieChartMetric(metric);

export const splitInsightMetrics = (metrics: {
  kpis: InsightTrackingMetric[];
  charts: InsightTrackingMetric[];
}) => ({
  kpiMetrics: metrics.kpis.filter(isScalarMetric),
  chartMetrics: metrics.charts.filter(isChartMetric),
});
